import { useCallback, useEffect, useState } from 'react'
import { ComboboxOption } from 'modules/ui/molecules/Combobox'

export type EmployerMatchingCompany = {
  id: number | string
  company_name: string
  subsidiaries?: string[]
  highlight?: {
    company_name?: string[]
    linkedin_names?: string[]
    subsidiaries?: string[]
  }
}

export type EmployerMatchingProvider = 'DOUBLE_THE_DONATION' | 'AMPLY'

interface UseEmployerMatchingSearchOptions {
  provider: EmployerMatchingProvider
  query: string
  enabled?: boolean
}

/**
 * Transforms Double the Donation data to <option> attributes format
 * @param companyData - The company data to map
 * @returns The mapped data
 */
const getDoubleTheDonationMappedData = (companyData: EmployerMatchingCompany): ComboboxOption => {
  const highlight = companyData.highlight
  const toAppendCompanyName = ` (${companyData.company_name})`

  const highlightName =
    highlight?.company_name && highlight.company_name.length > 0
      ? highlight.company_name[0]
      : highlight?.linkedin_names && highlight.linkedin_names.length > 0
      ? highlight.linkedin_names[0] + toAppendCompanyName
      : highlight?.subsidiaries && highlight.subsidiaries.length > 0
      ? highlight.subsidiaries[0] + toAppendCompanyName
      : companyData.company_name

  // Strip HTML tags from highlight results
  const span = document.createElement('span')
  span.innerHTML = highlightName
  const cleanLabel = span.textContent || span.innerText || companyData.company_name

  return {
    label: cleanLabel,
    value: companyData.id,
  }
}

/**
 * Transforms Amply data to <option> attributes format
 * @param companyName - The company name to map
 * @param index - The index of the company
 * @returns The mapped data
 */
const getAmplyMappedData = (companyName: string, index: number): ComboboxOption => {
  return {
    label: companyName,
    value: `amply_${index}`, // Generate a unique ID for Amply
  }
}

const useEmployerMatchingSearch = ({
  provider,
  query,
  enabled = true,
}: UseEmployerMatchingSearchOptions) => {
  const [data, setData] = useState<ComboboxOption[]>([])
  const [companies, setCompanies] = useState<EmployerMatchingCompany[]>([])
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  // Reset data when query changes
  useEffect(() => {
    setData([])
    setError(false)
  }, [query])

  const handleDoubleTheDonationSearch = useCallback(
    async (searchQuery: string) => {
      // Direct API call to Double the Donation (real-time search)
      const response = await fetch(
        `https://doublethedonation.com/api/v1/prefix?query=${encodeURIComponent(searchQuery)}`,
      )

      if (!response.ok) {
        throw new Error('Failed to fetch from Double the Donation')
      }

      const doubleTheDonationData = await response.json()
      setCompanies(doubleTheDonationData)

      const mappedData = doubleTheDonationData.map((company: any) =>
        getDoubleTheDonationMappedData(company),
      )

      setData(mappedData)
    },
    [setData, setCompanies],
  )

  const handleAmplySearch = useCallback(
    async (searchQuery: string) => {
      // Use the apiv2 /donation-matchers endpoint
      const response = await fetch('/embedded/api/donation-matchers')

      if (!response.ok) {
        throw new Error('Failed to fetch companies from Amply')
      }

      const amplyResponse = await response.json()
      const allCompanies = amplyResponse.data || []

      // Filter companies client-side
      const filteredCompanies = allCompanies
        .filter((companyName: string) =>
          companyName.toLowerCase().includes(searchQuery.toLowerCase()),
        )
        .slice(0, 20) // Limit results

      // Map to the expected format
      const mappedCompanies = filteredCompanies.map((companyName: string, index: number) => ({
        id: `amply_${index}`,
        company_name: companyName,
      }))

      setCompanies(mappedCompanies)

      const mappedData = filteredCompanies.map((companyName: string, index: number) =>
        getAmplyMappedData(companyName, index),
      )

      setData(mappedData)
    },
    [setData, setCompanies],
  )

  useEffect(() => {
    const fetchData = async () => {
      if (!query || !enabled || query.length < 2) {
        setLoading(false)
        return
      }

      setLoading(true)
      setError(false)

      try {
        if (provider === 'DOUBLE_THE_DONATION') {
          await handleDoubleTheDonationSearch(query)
        } else if (provider === 'AMPLY') {
          await handleAmplySearch(query)
        }
      } catch (err) {
        console.error(`Failed to fetch employer matching data for ${provider}:`, err)
        setError(true)
        setData([])
        setCompanies([])
      } finally {
        setLoading(false)
      }
    }

    const debounceTimer = setTimeout(fetchData, 300) // Debounce search

    return () => clearTimeout(debounceTimer)
  }, [query, provider, enabled])

  return {
    data,
    companies,
    error,
    loading,
    hasMore: false,
  }
}

export default useEmployerMatchingSearch
